import React, { useState } from "react";
import { Row, Col, Form, Button, Alert } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaBuilding,
  FaGlobe,
  FaPlus,
  FaTimes,
  FaInfoCircle,
} from "react-icons/fa";

const BusinessStep = ({
  formData,
  updateFormData,
}) => {
  const business = formData.business || {};
  const [beneficialOwners, setBeneficialOwners] = useState(
    business.beneficialOwners || []
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateFormData("business", { [name]: value });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    updateFormData("business", { [name]: checked });
  };

  const addBeneficialOwner = () => {
    const newOwner = {
      id: Date.now(),
      fullName: "",
      ownershipPercentage: "",
      nationality: "",
      dateOfBirth: "",
      idNumber: "",
    };
    const updatedOwners = [...beneficialOwners, newOwner];
    setBeneficialOwners(updatedOwners);
    updateFormData("business", { beneficialOwners: updatedOwners });
  };

  const removeBeneficialOwner = (id) => {
    const updatedOwners = beneficialOwners.filter((owner) => owner.id !== id);
    setBeneficialOwners(updatedOwners);
    updateFormData("business", { beneficialOwners: updatedOwners });
  };

  const updateBeneficialOwner = (id, field, value) => {
    const updatedOwners = beneficialOwners.map((owner) =>
      owner.id === id ? { ...owner, [field]: value } : owner
    );
    setBeneficialOwners(updatedOwners);
    updateFormData("business", { beneficialOwners: updatedOwners });
  };

  const countries = [
    "United States",
    "Canada",
    "United Kingdom",
    "Germany",
    "France",
    "Italy",
    "Spain",
    "Netherlands",
    "Switzerland",
    "Australia",
    "Japan",
    "Singapore",
    "Hong Kong",
    "Other",
  ];

  const businessTypes = [
    { value: "", label: "Select business type" },
    { value: "corporation", label: "Corporation" },
    { value: "llc", label: "Limited Liability Company (LLC)" },
    { value: "partnership", label: "Partnership" },
    { value: "sole-proprietorship", label: "Sole Proprietorship" },
    { value: "trust", label: "Trust" },
    { value: "foundation", label: "Foundation" },
    { value: "other", label: "Other" },
  ];

  const skipBusinessInfo = !business.hasBusinessInterest;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Form>
        <Row>
          <Col md={12}>
            <div className="form-group">
              <div className="form-check">
                <input
                  type="checkbox"
                  name="hasBusinessInterest"
                  checked={business.hasBusinessInterest || false}
                  onChange={handleCheckboxChange}
                  className="form-check-input"
                  id="hasBusinessInterest"
                />
                <label
                  className="form-check-label"
                  htmlFor="hasBusinessInterest"
                >
                  If you are registergin your trading account under a business
                  or corporation, please fill out the information below:
                </label>
              </div>
              <small className="form-text text-muted">
                Check this box if you own or have significant control over a
                business entity
              </small>
            </div>
          </Col>

          {business.hasBusinessInterest && (
            <>
              <Col md={12}>
                <hr className="my-4" />
                <h5 className="mb-3 d-flex align-items-center">
                  <FaBuilding className="me-2 text-primary" />
                  Business Information
                </h5>
              </Col>

              <Col md={8}>
                <div className="form-group">
                  <label className="form-label">
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={business.companyName || ""}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Enter company name"
                    required={business.hasBusinessInterest}
                  />
                </div>
              </Col>

              <Col md={4}>
                <div className="form-group">
                  <label className="form-label">Business Type</label>
                  <select
                    name="businessType"
                    value={business.businessType || ""}
                    onChange={handleChange}
                    className="form-control"
                  >
                    {businessTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>

              <Col md={12}>
                <div className="form-group">
                  <label className="form-label d-flex align-items-center">
                    <FaGlobe className="me-2 text-primary" />
                    Company Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={business.website || ""}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="https://www.example.com"
                  />
                  <small className="form-text text-muted">
                    Enter your company's website URL (optional)
                  </small>
                </div>
              </Col>

              <Col md={12}>
                <div className="form-group">
                  <label className="form-label">Company Address</label>
                  <input
                    type="text"
                    name="companyAddress"
                    value={business.companyAddress || ""}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Enter complete company address"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label className="form-label">Company Phone Number</label>
                  <input
                    type="tel"
                    name="companyPhone"
                    value={business.companyPhone || ""}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label className="form-label">Company Email</label>
                  <input
                    type="email"
                    name="companyEmail"
                    value={business.companyEmail || ""}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="info@company.com"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label className="form-label">Registration Number</label>
                  <input
                    type="text"
                    name="registrationNumber"
                    value={business.registrationNumber || ""}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Company registration number"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label className="form-label">Country of Incorporation</label>
                  <select
                    name="countryOfIncorporation"
                    value={business.countryOfIncorporation || ""}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">Select country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>

              <Col md={12}>
                <div className="form-group">
                  <label className="form-label">Nature of Business</label>
                  <textarea
                    name="natureOfBusiness"
                    value={business.natureOfBusiness || ""}
                    onChange={handleChange}
                    className="form-control"
                    rows="3"
                    placeholder="Describe the main business activities and industry"
                  />
                </div>
              </Col>

              {/* Beneficial Ownership Section */}
              <Col md={12}>
                <hr className="my-4" />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Ultimate Beneficial Owners (UBO)</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={addBeneficialOwner}
                  >
                    <FaPlus className="me-1" /> Add Owner
                  </Button>
                </div>

                <Alert variant="info" className="mb-3">
                  <FaInfoCircle className="me-2" />
                  Please declare all individuals who own or control 25% or more
                  of the company, or who exercise significant control over the
                  company's management.
                </Alert>
              </Col>

              {beneficialOwners.map((owner, index) => (
                <Col md={12} key={owner.id}>
                  <div className="beneficial-owner-card mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Beneficial Owner #{index + 1}</h6>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeBeneficialOwner(owner.id)}
                      >
                        <FaTimes />
                      </Button>
                    </div>

                    <Row>
                      <Col md={6}>
                        <div className="form-group">
                          <label className="form-label">Full Name</label>
                          <input
                            type="text"
                            value={owner.fullName}
                            onChange={(e) =>
                              updateBeneficialOwner(
                                owner.id,
                                "fullName",
                                e.target.value
                              )
                            }
                            className="form-control"
                            placeholder="Enter full name"
                          />
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="form-group">
                          <label className="form-label">
                            Ownership Percentage
                          </label>
                          <input
                            type="number"
                            value={owner.ownershipPercentage}
                            onChange={(e) =>
                              updateBeneficialOwner(
                                owner.id,
                                "ownershipPercentage",
                                e.target.value
                              )
                            }
                            className="form-control"
                            placeholder="25"
                            min="0"
                            max="100"
                          />
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="form-group">
                          <label className="form-label">Nationality</label>
                          <select
                            value={owner.nationality}
                            onChange={(e) =>
                              updateBeneficialOwner(
                                owner.id,
                                "nationality",
                                e.target.value
                              )
                            }
                            className="form-control"
                          >
                            <option value="">Select nationality</option>
                            {countries.map((country) => (
                              <option key={country} value={country}>
                                {country}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="form-group">
                          <label className="form-label">Date of Birth</label>
                          <input
                            type="date"
                            value={owner.dateOfBirth}
                            onChange={(e) =>
                              updateBeneficialOwner(
                                owner.id,
                                "dateOfBirth",
                                e.target.value
                              )
                            }
                            className="form-control"
                          />
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="form-group">
                          <label className="form-label">ID Number</label>
                          <input
                            type="text"
                            value={owner.idNumber}
                            onChange={(e) =>
                              updateBeneficialOwner(
                                owner.id,
                                "idNumber",
                                e.target.value
                              )
                            }
                            className="form-control"
                            placeholder="Passport/ID number"
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              ))}

              {beneficialOwners.length === 0 && (
                <Col md={12}>
                  <div className="text-center py-4 text-muted">
                    <p>No beneficial owners added yet.</p>
                    <p>
                      Click "Add Owner" to declare beneficial ownership
                      information.
                    </p>
                  </div>
                </Col>
              )}
            </>
          )}

          {skipBusinessInfo && (
            <Col md={12}>
              <div className="text-center py-5 text-muted">
                <FaBuilding size={48} className="mb-3 opacity-50" />
                <p>No business information required.</p>
                <p>You can proceed to the next step.</p>
              </div>
            </Col>
          )}
        </Row>
      </Form>
    </motion.div>
  );
};

export default BusinessStep;
